// src/components/HelpCenter.jsx
import React from "react";
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Divider,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import SecurityIcon from "@mui/icons-material/Security";
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import CandlestickChartIcon from "@mui/icons-material/CandlestickChart";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import TroubleshootIcon from "@mui/icons-material/Troubleshoot";
import TimelineIcon from "@mui/icons-material/Timeline";
import SpeedIcon from "@mui/icons-material/Speed";
import ShieldIcon from "@mui/icons-material/Shield";
import { useSEO, useJsonLD } from "./SEO";

const BINANCE_YELLOW = "#F0B90B";
const BORDER = "rgba(255,255,255,0.08)";
const TEXT = "#EAEFF2";
const MUTED = "#B0BEC5";
const CARD_BG = "rgba(38,50,56,0.85)"; // #263238 مع شفافية طفيفة + blur

const categories = [
  {
    icon: <AccountBoxIcon />,
    title: "Account",
    items: [
      "Create account",
      "Email updates",
      "Forgot password",
      "Close / Reopen account",
    ],
  },
  {
    icon: <SecurityIcon />,
    title: "Security",
    items: [
      "2FA (TOTP/SMS)",
      "KYC/AML verification",
      "Withdrawal allow-list",
      "Recognize phishing",
    ],
  },
  {
    icon: <MonetizationOnIcon />,
    title: "Deposits & Withdrawals",
    items: [
      "Deposit crypto (networks)",
      "Fees & confirmations",
      "Pending / Delayed withdrawals",
      "Address book & tags (Memo/Tag)",
    ],
  },
  {
    icon: <CandlestickChartIcon />,
    title: "Spot & Futures",
    items: [
      "What is Spot?",
      "Perpetuals 101",
      "Leverage & Margin",
      "Funding & Liquidation",
    ],
  },
];

const faq = [
  {
    q: "How do I enable Two-Factor Authentication (2FA)?",
    a:
      "Go to Settings → Security → Two-Factor Authentication, choose TOTP (Google Authenticator) or SMS, then follow the on-screen steps. Keep backup codes safe and never share them.",
  },
  {
    q: "What are the trading fees and how are tiers calculated?",
    a:
      "Fees follow a maker/taker schedule based on your 30-day volume and/or VIP tier. Visit the Fees page for the live table and examples. VIP users may receive additional discounts.",
  },
  {
    q: "Why is my withdrawal pending?",
    a:
      "Typical reasons include network congestion, AML/KYC reviews, security holds after password or 2FA changes, or incorrect tags/memos. Check the transaction hash and your email for alerts.",
  },
  {
    q: "How do I open a Perpetual Futures position safely?",
    a:
      "Transfer funds to the Futures wallet, set leverage conservatively, and use stop-loss/take-profit. Understand funding, maintenance margin, and liquidation before trading.",
  },
  {
    q: "How can I contact support if I can’t access my account?",
    a:
      "Use the identity recovery form from the Help Center or reach Live Chat. We may request KYC documents to verify ownership for your security.",
  },
];

export default function HelpCenter() {
  useSEO({
    title: "Help Center — 1CryptoX | Guides, Security, FAQs, Support",
    description:
      "1CryptoX Help Center: account setup, security (2FA, KYC/AML), deposits & withdrawals, spot & perpetual futures guides, fees, troubleshooting, and 24/7 live chat.",
    keywords: [
      "help center",
      "2FA",
      "KYC AML",
      "crypto deposits",
      "withdrawals",
      "fees",
      "spot trading",
      "perpetual futures",
      "margin leverage",
      "live chat support",
      "knowledge base",
    ],
    canonical: "https://www.1cryptox.com/help-center",
  });

  // FAQ JSON-LD (غني ومحسّن)
  useJsonLD("faq", {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  });

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        px: { xs: 2, md: 4 },
        py: { xs: 4, md: 6 },
        position: "relative",
        overflow: "hidden",
        // خلفية رئيسية مطابقة للـ Welcome
        background:
          "radial-gradient(60% 60% at 70% 30%, rgba(240,185,11,0.10) 0%, rgba(240,185,11,0.03) 100%)",
        bgcolor: "#0B0E11",
      }}
    >
      <Container maxWidth="lg" disableGutters>
        {/* Hero / Search */}
        <Card
          sx={{
            bgcolor: CARD_BG,
            border: `1px solid ${BORDER}`,
            borderRadius: 2,
            backdropFilter: "blur(8px)",
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Typography
              variant="h3"
              sx={{ fontWeight: 900, color: TEXT, mb: 1 }}
            >
              Help Center
            </Typography>
            <Typography sx={{ color: MUTED, mb: 2 }}>
              Welcome to the 1CryptoX Knowledge Base. Explore guided articles,
              deep-dive tutorials, security best practices, and live support —
              built to help you trade confidently on Spot and Perpetual Futures.
              If you’re new, start with account security and deposits. If you’re
              advanced, explore margin, funding, and liquidation logic.
            </Typography>

            {/* <TextField
              fullWidth
              placeholder="Search guides, fees, KYC, deposits, futures..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            /> */}

            {/* Quick suggestions */}
            <Stack
              direction="row"
              spacing={1}
              sx={{ mt: 1.25, flexWrap: "wrap" }}
            >
              {[
                "Enable 2FA",
                "Verify identity (KYC)",
                "Deposit USDT on TRON",
                "Spot vs Perpetuals",
                "Fees & VIP tiers",
                "Withdrawal pending",
              ].map((s) => (
                <Chip
                  key={s}
                  label={s}
                  size="small"
                  sx={{
                    mr: 0.75,
                    mb: 0.75,
                    bgcolor: "rgba(255,255,255,0.06)",
                    border: `1px solid ${BORDER}`,
                    color: TEXT,
                    "&:hover": { bgcolor: "rgba(255,255,255,0.10)" },
                  }}
                />
              ))}
            </Stack>
          </CardContent>
        </Card>

        {/* Quick Links (Top Solutions) */}
        <Grid container spacing={2} sx={{ mt: 2 }}>
          {[
            {
              icon: <VerifiedUserIcon />,
              title: "KYC & Account Verification",
              desc:
                "Complete identity verification to unlock higher limits and faster withdrawals. Learn accepted documents, review times, and how to fix common issues.",
              href: "/help-center/kyc",
            },
            {
              icon: <ReceiptLongIcon />,
              title: "Fees & VIP Tiers",
              desc:
                "Understand maker/taker fees, VIP thresholds, monthly volume calculations, and additional discounts that may apply to your tier.",
              href: "/fees",
            },
            {
              icon: <TroubleshootIcon />,
              title: "Troubleshooting & Status",
              desc:
                "Check system status, common errors, and step-by-step fixes for deposits, withdrawals, price feeds, and trading connectivity.",
              href: "/status",
            },
          ].map((card, i) => (
            <Grid item xs={12} md={4} key={i}>
              <Card
                sx={{
                  height: "100%",
                  bgcolor: CARD_BG,
                  border: `1px solid ${BORDER}`,
                  borderRadius: 2,
                }}
              >
                <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                  <Stack direction="row" spacing={1.25} alignItems="flex-start">
                    <Box
                      sx={{
                        width: 46,
                        height: 46,
                        borderRadius: "999px",
                        bgcolor: "rgba(255,255,255,0.06)",
                        border: `1px solid ${BORDER}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: BINANCE_YELLOW,
                        "& svg": { fontSize: 24 },
                      }}
                    >
                      {card.icon}
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 800, color: TEXT }}>
                        {card.title}
                      </Typography>
                      <Typography sx={{ color: MUTED, mt: 0.5 }}>
                        {card.desc}
                      </Typography>
                      <Button
                        href={card.href}
                        size="small"
                        sx={{ mt: 1, color: BINANCE_YELLOW, fontWeight: 700 }}
                      >
                        Learn more
                      </Button>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Categories */}
        <Grid container spacing={2} sx={{ mt: 2 }}>
          {categories.map((cat, idx) => (
            <Grid key={idx} item xs={12} md={3}>
              <Card
                sx={{
                  height: "100%",
                  bgcolor: CARD_BG,
                  border: `1px solid ${BORDER}`,
                  borderRadius: 2,
                }}
              >
                <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                  <Typography
                    variant="h6"
                    sx={{
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                      fontWeight: 800,
                      color: TEXT,
                    }}
                  >
                    <span style={{ color: BINANCE_YELLOW }}>{cat.icon}</span>
                    {cat.title}
                  </Typography>
                  <Box sx={{ mt: 1.5 }}>
                    {cat.items.map((it) => (
                      <Chip
                        key={it}
                        label={it}
                        size="small"
                        sx={{
                          mr: 1,
                          mb: 1,
                          bgcolor: "rgba(255,255,255,0.06)",
                          border: `1px solid ${BORDER}`,
                          color: TEXT,
                          "&:hover": { bgcolor: "rgba(255,255,255,0.10)" },
                        }}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Guided sections */}
        <Grid container spacing={2} sx={{ mt: 2 }}>
          {/* Deposits & Withdrawals Guide */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: "100%",
                bgcolor: CARD_BG,
                border: `1px solid ${BORDER}`,
                borderRadius: 2,
              }}
            >
              <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 800, color: TEXT, mb: 0.5 }}
                >
                  Deposits & Withdrawals — Best Practices
                </Typography>
                <Typography sx={{ color: MUTED, mb: 1.5 }}>
                  Ensure seamless transfers with correct networks, sufficient
                  confirmations, and accurate memos/tags. Reconcile fees and
                  minimums before sending funds. Add trusted addresses to your
                  allow-list for faster processing.
                </Typography>
                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                  {[
                    "Network differences (TRON / ETH / BSC)",
                    "How confirmations work",
                    "Memo/Tag & Address book",
                    "Pending & holds explained",
                  ].map((t) => (
                    <Chip
                      key={t}
                      label={t}
                      size="small"
                      sx={{
                        mr: 1,
                        mb: 1,
                        bgcolor: "rgba(255,255,255,0.06)",
                        border: `1px solid ${BORDER}`,
                        color: TEXT,
                      }}
                    />
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Trading Tutorials */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: "100%",
                bgcolor: CARD_BG,
                border: `1px solid ${BORDER}`,
                borderRadius: 2,
              }}
            >
              <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 800, color: TEXT, mb: 0.5 }}
                >
                  Trading Tutorials — Spot & Perpetual Futures
                </Typography>
                <Typography sx={{ color: MUTED, mb: 1.5 }}>
                  Build confidence with guided flows: from placing your first
                  Spot order to managing leveraged positions with stop-loss and
                  take-profit. Master funding, margin, and risk to avoid
                  liquidation during volatility.
                </Typography>
                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                  {[
                    "Place market/limit orders",
                    "Leverage & isolated vs cross",
                    "Funding rate mechanics",
                    "Stop-loss & take-profit",
                  ].map((t) => (
                    <Chip
                      key={t}
                      label={t}
                      size="small"
                      sx={{
                        mr: 1,
                        mb: 1,
                        bgcolor: "rgba(255,255,255,0.06)",
                        border: `1px solid ${BORDER}`,
                        color: TEXT,
                      }}
                    />
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* FAQ */}
        <Box sx={{ mt: 3 }}>
          <Card
            sx={{
              bgcolor: CARD_BG,
              border: `1px solid ${BORDER}`,
              borderRadius: 2,
            }}
          >
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Typography
                variant="h5"
                sx={{ fontWeight: 900, color: TEXT, mb: 1 }}
              >
                Frequently Asked Questions
              </Typography>
              <Typography sx={{ color: MUTED, mb: 2 }}>
                Find quick answers to the most common questions around security,
                fees, deposits & withdrawals, and trading mechanics.
              </Typography>

              {faq.map((f, i) => (
                <Accordion
                  key={i}
                  disableGutters
                  sx={{
                    bgcolor: "transparent",
                    borderTop: i === 0 ? `1px solid ${BORDER}` : "none",
                    borderBottom: `1px solid ${BORDER}`,
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: MUTED }} />}
                  >
                    <Typography sx={{ fontWeight: 700, color: TEXT }}>
                      {f.q}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography sx={{ color: MUTED }}>{f.a}</Typography>
                  </AccordionDetails>
                </Accordion>
              ))}

              {/* Risk disclaimer for Perpetuals */}
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  mt: 2,
                  p: 1.25,
                  borderRadius: 1.5,
                  border: `1px solid ${BORDER}`,
                  bgcolor: "rgba(255,255,255,0.04)",
                }}
              >
                <HelpOutlineIcon sx={{ color: BINANCE_YELLOW }} />
                <Typography sx={{ color: MUTED, fontSize: 13.5 }}>
                  Perpetual Futures are complex instruments and carry a high
                  risk of rapid loss. Always manage leverage conservatively and
                  use risk controls.
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Box>

        {/* Live Support CTA */}
        <Card
          sx={{
            mt: 3,
            bgcolor: CARD_BG,
            border: `1px solid ${BORDER}`,
            borderRadius: 2,
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                mb: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: TEXT,
              }}
            >
              <SupportAgentIcon /> 24/7 Live Support
            </Typography>
            <Typography sx={{ color: MUTED, mb: 1.25 }}>
              Our global support team provides real-time assistance via chat and
              tickets. Average first response under 2 minutes. For
              security-sensitive requests, we may ask for additional
              verification to protect your account.
            </Typography>
            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
              <Chip
                label="Live Chat"
                sx={{
                  bgcolor: "rgba(240,185,11,0.12)",
                  color: BINANCE_YELLOW,
                  border: `1px solid rgba(240,185,11,0.25)`,
                  fontWeight: 700,
                }}
              />
              <Chip
                label="Open Ticket"
                variant="outlined"
                sx={{ color: TEXT, borderColor: BORDER, fontWeight: 700 }}
              />
              <Chip
                label="System Status"
                variant="outlined"
                sx={{ color: TEXT, borderColor: BORDER, fontWeight: 700 }}
                component="a"
                href="/status"
                clickable
              />
            </Stack>

            <Divider sx={{ my: 2, borderColor: BORDER }} />

            <Grid container spacing={2}>
              {[
                {
                  icon: <ShieldIcon />,
                  t: "Security-first",
                  d:
                    "Multi-layer protection: encryption, 2FA, device management, and allow-listed withdrawals.",
                },
                {
                  icon: <SpeedIcon />,
                  t: "Performance",
                  d:
                    "Low-latency matching, robust APIs, and a reliable infrastructure optimized for uptime.",
                },
                {
                  icon: <TimelineIcon />,
                  t: "Transparency",
                  d:
                    "Clear fees, explicit funding mechanics, and detailed logs so you always stay in control.",
                },
              ].map((k, idx) => (
                <Grid item xs={12} md={4} key={idx}>
                  <Stack direction="row" spacing={1.25} alignItems="flex-start">
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: "999px",
                        bgcolor: "rgba(255,255,255,0.06)",
                        border: `1px solid ${BORDER}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: BINANCE_YELLOW,
                        "& svg": { fontSize: 22 },
                      }}
                    >
                      {k.icon}
                    </Box>
                    <Box>
                      <Typography sx={{ color: TEXT, fontWeight: 800 }}>
                        {k.t}
                      </Typography>
                      <Typography sx={{ color: MUTED, mt: 0.25 }}>
                        {k.d}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
